<template>
  <!-- Panel w rogu, NIE okno modalne: lekarz w trakcie rozmowy widzi kartę i transkrypcję. -->
  <div v-if="open" class="telewizyta-panel shadow">
    <div class="d-flex align-items-center justify-content-between mb-2">
      <strong>
        <font-awesome-icon icon="phone" class="mr-1" />
        Telewizyta
      </strong>
      <b-button size="sm" variant="link" :disabled="wPolaczeniu" @click="$emit('close')">×</b-button>
    </div>

    <div class="small text-muted mb-2">
      Numer z karty pacjenta{{ numer ? ` (${numer})` : '' }} · dzwonimy z numeru przychodni
    </div>

    <div class="stan mb-2" :class="`stan-${stan}`">
      {{ opisStanu }}<span v-if="stan === 'rozmowa'"> · {{ czasRozmowy }}</span>
    </div>
    <div v-if="blad" class="alert alert-danger py-1 px-2 small mb-2">{{ blad }}</div>
    <div v-if="stan === 'rozmowa'" class="small text-muted mb-2">
      Transkrypcja włączona — poinformuj pacjenta, że rozmowa jest zapisywana do dokumentacji.
    </div>

    <div class="d-flex">
      <b-button v-if="!wPolaczeniu" size="sm" variant="success" class="mr-2" @click="zadzwon">
        <font-awesome-icon icon="phone" class="mr-1" /> Zadzwoń
      </b-button>
      <template v-else>
        <b-button size="sm" variant="danger" class="mr-2" @click="rozlacz">
          <font-awesome-icon icon="phone-slash" class="mr-1" /> Rozłącz
        </b-button>
        <b-button size="sm" variant="outline-secondary" :disabled="stan !== 'rozmowa'" @click="przelaczMikrofon">
          {{ wyciszony ? 'Włącz mikrofon' : 'Wycisz' }}
        </b-button>
      </template>
    </div>
  </div>
</template>

<script>
import telewizytaApi from '../services/telewizytaApi'

// livekit-client w tej samej wersji co panel recepcji medapi; UMD, bo webpack 4 tej aplikacji
// nie przetworzy składni paczki ESM. Ładowany dopiero przy pierwszym połączeniu.
const LIVEKIT_UMD = 'https://cdn.jsdelivr.net/npm/livekit-client@2.22.3/dist/livekit-client.umd.js'
let ladowanie = null
function livekit() {
  if (window.LivekitClient) return Promise.resolve(window.LivekitClient)
  if (!ladowanie) {
    ladowanie = new Promise((resolve, reject) => {
      const s = document.createElement('script')
      s.src = LIVEKIT_UMD
      s.async = true
      s.onload = () => (window.LivekitClient ? resolve(window.LivekitClient) : reject(new Error('Nie wczytano biblioteki połączeń')))
      s.onerror = () => {
        ladowanie = null
        reject(new Error('Nie wczytano biblioteki połączeń (sieć?)'))
      }
      document.head.appendChild(s)
    })
  }
  return ladowanie
}

export default {
  name: 'TelewizytaPanel',
  props: {
    open: { type: Boolean, default: false },
    patientId: { type: [String, Number], required: true }
  },
  data: () => ({
    stan: 'gotowe', // gotowe | laczenie | dzwonie | rozmowa | rozlaczono | blad
    blad: '',
    numer: '',
    pokoj: '',
    wyciszony: false,
    start: 0,
    teraz: 0,
    zegar: null
  }),
  computed: {
    wPolaczeniu() {
      return ['laczenie', 'dzwonie', 'rozmowa'].includes(this.stan)
    },
    opisStanu() {
      return {
        gotowe: 'Gotowe do połączenia',
        laczenie: 'Łączę…',
        dzwonie: 'Dzwonię… (w słuchawce sygnał, do 30 s)',
        rozmowa: 'Rozmowa',
        rozlaczono: 'Rozłączono',
        blad: 'Nie połączono'
      }[this.stan]
    },
    czasRozmowy() {
      const s = Math.max(0, Math.floor((this.teraz - this.start) / 1000))
      return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
    }
  },
  beforeDestroy() {
    this.sprzataj()
  },
  methods: {
    async zadzwon() {
      this.blad = ''
      this.stan = 'laczenie'
      this.elementy = []
      // okno transkrypcji otwiera się od razu, jak przy mikrofonie; nagrywanie rusza po odebraniu
      this.$emit('start')
      try {
        this.ctx = new (window.AudioContext || window.webkitAudioContext)()
        this.miks = this.ctx.createMediaStreamDestination()
        if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {})
        const LK = await livekit()
        const bilet = await telewizytaApi.zadzwon(this.patientId)
        this.numer = bilet.numer || ''
        this.pokoj = bilet.pokoj

        const room = new LK.Room()
        this.room = room
        room.on(LK.RoomEvent.TrackSubscribed, (track) => {
          if (track.kind !== 'audio') return
          const el = track.attach() // głos pacjenta (i sygnał dzwonienia) w głośniku
          el.style.display = 'none'
          document.body.appendChild(el)
          this.elementy.push(el)
          this.dolaczDoMiksu(track.mediaStreamTrack)
        })
        room.on(LK.RoomEvent.ParticipantDisconnected, () => {
          // pacjent odłożył słuchawkę
          if (this.stan === 'rozmowa') this.rozlacz()
        })
        room.on(LK.RoomEvent.Disconnected, () => {
          if (this.wPolaczeniu) this.zakoncz('rozlaczono')
        })
        await room.connect(bilet.url, bilet.token)
        await room.localParticipant.setMicrophoneEnabled(true)
        const mojMikrofon = room.localParticipant.getTrackPublication(LK.Track.Source.Microphone)
        if (mojMikrofon?.track) this.dolaczDoMiksu(mojMikrofon.track.mediaStreamTrack)

        this.stan = 'dzwonie'
        await telewizytaApi.polacz(this.pokoj, this.patientId)
        this.stan = 'rozmowa'
        this.start = Date.now()
        this.teraz = this.start
        this.zegar = setInterval(() => (this.teraz = Date.now()), 1000)
        // od tej chwili transkrypcja: ten sam tor co nagranie wizyty w gabinecie
        this.$emit('strumien', this.miks.stream)
      } catch (e) {
        // lekarz rozłączył w trakcie dzwonienia — to nie błąd, tylko koniec
        if (this.stan === 'rozlaczono') return
        this.blad = e.message
        await this.zakoncz('blad')
      }
    },
    /** lekarz + pacjent w jeden strumień — tak jak mikrofon w gabinecie słyszy obie osoby */
    dolaczDoMiksu(mediaStreamTrack) {
      if (!this.ctx) return
      this.ctx.createMediaStreamSource(new MediaStream([mediaStreamTrack])).connect(this.miks)
    },
    async przelaczMikrofon() {
      if (!this.room) return
      this.wyciszony = !this.wyciszony
      await this.room.localParticipant.setMicrophoneEnabled(!this.wyciszony)
    },
    async rozlacz() {
      const pokoj = this.pokoj
      await this.zakoncz('rozlaczono')
      if (pokoj) await telewizytaApi.rozlacz(pokoj)
    },
    async zakoncz(stan) {
      this.stan = stan
      this.$emit('strumien', null)
      this.sprzataj()
    },
    sprzataj() {
      if (this.zegar) clearInterval(this.zegar)
      this.zegar = null
      if (this.room) {
        const r = this.room
        this.room = null
        r.disconnect().catch(() => {})
      }
      ;(this.elementy || []).forEach((el) => el.remove())
      this.elementy = []
      if (this.ctx) {
        this.ctx.close().catch(() => {})
        this.ctx = null
        this.miks = null
      }
      this.pokoj = ''
      this.wyciszony = false
    }
  }
}
</script>

<style scoped>
.telewizyta-panel {
  position: fixed;
  right: 16px;
  bottom: 16px;
  width: 320px;
  z-index: 1060; /* nad oknem transkrypcji (b-modal 1050) */
  background: #fff;
  border: 1px solid #d7dde5;
  border-radius: 6px;
  padding: 12px;
}
.stan {
  font-weight: 600;
}
.stan-rozmowa {
  color: #0f766e;
}
.stan-blad {
  color: #b91c1c;
}
</style>
